<script setup lang="ts">
/**
 * This is a simple HTML editor component that uses Quill as the editor component
 *
 * @see https://github.com/alekswebnet/vue-quilly For the plugin we use to create the WYSIWYG editor this wraps the Quill editor
 * @see https://quilljs.com/docs/quickstart/ For the Quill documentation
 *
 */
import { computed, onMounted, ref } from 'vue';
import { QuillyEditor } from 'vue-quilly';
import { randomstring } from '@/utilities/utils';
import { $tm } from '@/locize';
import Quill from 'quill';
import { SourceEditor } from '@/components';

import 'quill/dist/quill.core.css'; // Required
import 'quill/dist/quill.snow.css'; // For snow theme (optional)
import type { IEditorComponent } from '@/objects/gui';

const props = withDefaults(
    defineProps<{
        /**
         * The placeholder text
         */
        placeholder?: string;

        /**
         * If we should show a button to switch to source code editing
         */
        showSourceeditor?: boolean;

        /**
         * If we should show a button to switch to fullscreen
         * This is convenient if you want to edit the text in fullscreen mode. In modals it will use the entire modal
         * @default true
         */
        showFullscreen?: boolean;

        /**
         * This is the assigned ID if it is set from detail comp
         */
        id?: string;
    }>(),
    {
        placeholder: '',

        showSourceeditor: true,

        showFullscreen: true,
    },
);

/**
 * Set up the modelValue model with the internal value
 */
const value = defineModel<string | null>({ required: true });

/**
 * Define emits
 */
const emits = defineEmits<{
    (e: 'fillcontainer', value: boolean, id?: string): void;
}>();

/**
 * Define a Quill instance
 */
let quill: Quill | null = null;

const editorwrapper = ref<HTMLDivElement>();

/**
 * A handle to the Quill editor
 */
const editor = ref<InstanceType<typeof QuillyEditor>>();

/**
 * Define an ID for the editor. We need this one in order to add a custom button to the toolbar
 */
const guid = randomstring('q-', '');

/**
 * Define a model for the source code editor
 */
const source = ref<string>('');

const toolbarOptions = [
    ['bold', 'italic', 'underline'],
    [{ script: 'super' }, { script: 'sub' }],
    [{ header: '1' }, { header: '2' }, 'blockquote', 'code-block'],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['link', 'image'],
    /*['clean'],*/
];

/**
 * We define a new button for the toolbar if we want to show the source editor
 */
if (props.showSourceeditor) {
    let icons: any = Quill.import('ui/icons');
    icons['source'] =
        '<svg class="ql-fill" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.6.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc.--><path d="M512 32L0 32 0 480l512 0 0-448zM209 209l-47 47 47 47 17 17L192 353.9l-17-17-64-64-17-17 17-17 64-64 17-17L225.9 192l-17 17zM337 175l64 64 17 17-17 17-64 64-17 17L286.1 320l17-17 47-47-47-47-17-17L320 158.1l17 17z"/></svg>';

    toolbarOptions.push(['source']);
}

if (props.showFullscreen) {
    let icons: any = Quill.import('ui/icons');
    icons['fullscreen'] =
        '<svg class="ql-fill" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><!--!Font Awesome Pro 6.6.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc.--><path d="M144 32l16 0 0 32-16 0L32 64l0 112 0 16L0 192l0-16L0 48 0 32l16 0 128 0zM0 336l0-16 32 0 0 16 0 112 112 0 16 0 0 32-16 0L16 480 0 480l0-16L0 336zM432 32l16 0 0 16 0 128 0 16-32 0 0-16 0-112L304 64l-16 0 0-32 16 0 128 0zM416 336l0-16 32 0 0 16 0 128 0 16-16 0-128 0-16 0 0-32 16 0 112 0 0-112z"/></svg>';
    toolbarOptions.push(['fullscreen']);
}

/**
 * Define options for the Quill editor
 */
const options = ref({
    theme: 'snow',
    modules: {
        toolbar: toolbarOptions,
    },
    placeholder: props.placeholder != '' ? $tm(props.placeholder) : '',
    readOnly: false,
});

/**
 * Define a flag to indicate if we are editing the source code
 */
const isSourceEditing = ref(false);

const isFullscreen = ref(false);

onMounted(() => {
    quill = editor.value?.initialize(Quill)!;

    // A bit hacky way to add a custom button to the toolbar
    // There is a way to redesign the toolbar completely with a template, but this is a bit easier
    if (props.showSourceeditor) {
        let sourceButton = editorwrapper.value!.querySelector('.ql-source') as HTMLElement;
        sourceButton?.addEventListener('click', () => {
            isSourceEditing.value = !isSourceEditing.value;
            if (isSourceEditing.value) {
                source.value = value.value ?? '';
            } else {
                value.value = source.value;
            }
        });
    }

    if (props.showFullscreen) {
        let fullscreenButton = editorwrapper.value!.querySelector('.ql-fullscreen') as HTMLElement;
        fullscreenButton?.addEventListener('click', () => {
            isFullscreen.value = !isFullscreen.value;
            emits('fillcontainer', isFullscreen.value, props.id);
        });
    }
});

defineExpose<IEditorComponent>({ focus: () => null, validateMessage: computed(() => null) });
</script>

<template>
    <div
        class="form-resizeable form-control-htmleditor"
        :data-id="guid"
        ref="editorwrapper"
        :class="[
            isSourceEditing ? 'is-source-editing' : '',
            props.showSourceeditor || props.showFullscreen ? 'custom-buttons' : '',
            `custom-buttons-${(props.showSourceeditor ? 1 : 0) + (props.showFullscreen ? 1 : 0)}`,
        ]"
    >
        <QuillyEditor
            ref="editor"
            v-model="value"
            :options="options"
        />

        <SourceEditor
            v-if="isSourceEditing"
            :initiallines="5"
            v-model="source"
            @blur="value = source"
        />
    </div>
</template>
